import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Layers3,
  MapPin,
  Radio,
  RefreshCw,
  Route,
  Search,
  Settings2,
  ShieldAlert,
} from 'lucide-react';
import './ForesightDecisionQueue.css';
import HazardDictionaryManager from './HazardDictionaryManager';
import { DEFAULT_HAZARD_IDS, HAZARD_FILTERS } from './externalEvents';
import {
  claimSourceLabel,
  claimStatusLabel,
  collectEvidence,
  collectEvidenceActivity,
  dedupeClaims,
  evidencePublisher,
  humanizeClaim,
} from './foresightEvidence';
import {
  primaryClassificationOf,
  writtenLocationOf,
} from './foresightClassification';
import {
  operationalScopeOf,
  uniqueEventAreaLabels,
} from './foresightScope';

const HAZARD_PREFS_KEY = 'foresightHazardTypes';

const loadVisibleHazardIds = () => {
  if (typeof window === 'undefined') return DEFAULT_HAZARD_IDS;
  try {
    const saved = JSON.parse(window.localStorage.getItem(HAZARD_PREFS_KEY) || 'null');
    if (Array.isArray(saved)) {
      const validIds = new Set(HAZARD_FILTERS.map((hazard) => hazard.id));
      return [...new Set(saved.map((id) => String(id).toLowerCase()).filter((id) => validIds.has(id)))];
    }
  } catch (error) {
    // Use the established default set when the stored preference is invalid.
  }
  return DEFAULT_HAZARD_IDS;
};

const hazardGateOf = (item) => item?.hazard_gate || {};

const gateStatusOf = (item) => String(hazardGateOf(item)?.status || '').toLowerCase();

const isUnclassifiedPotentialHazard = (item) => {
  const hasNoKnownHazard = Array.isArray(item?.hazards) && item.hazards.length === 0;
  if (!hasNoKnownHazard) return false;

  const review = item?.classification_review || {};
  const status = String(
    item?.classification_status ||
    review?.status ||
    ''
  ).trim().toLowerCase().replaceAll(' ', '_');

  return (
    review?.required === true ||
    status === 'unclassified_potential_hazard' ||
    status === 'unclassified' ||
    status === 'needs_hazard_classification' ||
    status === 'review_required' ||
    status === 'requires_review'
  );
};

const isActionableHazard = (item) => {
  if (isUnclassifiedPotentialHazard(item)) return true;
  const gate = hazardGateOf(item);
  if (!gate || Object.keys(gate).length === 0) return true;
  if (gate.actionable === false) return false;
  return !['monitoring_evidence', 'rejected_non_hazard'].includes(gateStatusOf(item));
};

const displayTitleOf = (item) =>
  hazardGateOf(item)?.normalized_title || item?.normalized_title || item?.title || 'Detected hazard';

const firstNumber = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return null;
};

const summaryNumber = (summary, keys, fallback) => {
  for (const key of keys) {
    const value = summary?.[key];
    if (Number.isFinite(Number(value))) return Number(value);
  }
  return fallback;
};

const listOf = (...values) => {
  for (const value of values) {
    if (Array.isArray(value)) return value;
  }
  return [];
};

const hazardIdOf = (hazard) => String(
  typeof hazard === 'string'
    ? hazard
    : hazard?.id || hazard?.value || hazard?.name || ''
).trim().toLowerCase();

const hazardLabelOf = (hazard) => (
  hazard?.label ||
  hazard?.label_en ||
  hazard?.name ||
  hazardIdOf(hazard).replaceAll('_', ' ')
);

const HAZARD_COLORS = new Map(Object.entries({
  earthquake: '#f97316',
  fire: '#ef4444',
  flood: '#0ea5e9',
  storm: '#2563eb',
  sandstorm: '#d97706',
  heat: '#f59e0b',
  cold: '#38bdf8',
  volcano: '#dc2626',
  tsunami: '#0891b2',
  drought: '#a16207',
  landslide: '#78716c',
  disease: '#16a34a',
  biological: '#059669',
  radiation: '#ca8a04',
  chemical: '#65a30d',
  conflict: '#b91c1c',
  terrorism: '#991b1b',
  accident: '#ea580c',
  transport: '#475569',
  aviation: '#0284c7',
  maritime: '#0369a1',
  power: '#eab308',
  cyber: '#0f766e',
  pollution: '#64748b',
}));

const hazardCoverageText = (hazard) => {
  const coverage = hazard?.coverage || {};
  if (coverage.connected === false) return 'Coverage gap · no connected source';
  const sourceCount = firstNumber(coverage.source_count) ?? 0;
  const healthy = firstNumber(coverage.healthy) ?? 0;
  const degraded = firstNumber(coverage.degraded) ?? 0;
  if (sourceCount <= 0) return coverage.connected ? 'Connected source coverage' : 'Coverage not reported';
  const health = [
    healthy > 0 ? `${healthy} healthy` : '',
    degraded > 0 ? `${degraded} degraded` : '',
  ].filter(Boolean).join(', ');
  return `${sourceCount} connected ${sourceCount === 1 ? 'source' : 'sources'}${health ? ` · ${health}` : ''}`;
};

const itemHazardIds = (item) => listOf(item?.hazards).map(hazardIdOf).filter(Boolean);

const displayValue = (value) => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) return value.map(displayValue).filter(Boolean).join(', ');
  return value?.label || value?.display_name || value?.name || value?.origin_key || value?.text || '';
};

const textOf = (value) => {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return '';
  const direct = value.text || value.fact || value.claim || value.statement || value.description || value.title;
  if (direct) return direct;
  return [
    displayValue(value.subject),
    displayValue(value.predicate).replaceAll('_', ' '),
    displayValue(value.value),
  ].filter(Boolean).join(' ');
};

const eventTime = (item) => item?.updated_at || item?.occurred_at || item?.created_at || null;

const formatDateTime = (value) => {
  if (!value) return 'Time unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Time unavailable';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatLatency = (seconds) => {
  const value = firstNumber(seconds);
  if (value === null) return null;
  if (value < 60) return `${Math.max(0, Math.round(value))}s`;
  if (value < 3600) {
    const minutes = Math.floor(value / 60);
    const remaining = Math.round(value % 60);
    return remaining ? `${minutes}m ${remaining}s` : `${minutes}m`;
  }
  const hours = Math.floor(value / 3600);
  const minutes = Math.round((value % 3600) / 60);
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

const itemId = (item, index) =>
  String(item?.id || item?.event_key || item?.warning_id || item?.external_id || index);

const coordinatePairOf = (item) => {
  const gps = item?.gps
    || item?.location?.gps
    || item?.location_resolution?.gps;
  const latitude = Number(gps?.latitude ?? gps?.lat);
  const longitude = Number(gps?.longitude ?? gps?.lng ?? gps?.lon);
  if (
    !Number.isFinite(latitude)
    || !Number.isFinite(longitude)
    || latitude < -90
    || latitude > 90
    || longitude < -180
    || longitude > 180
  ) return null;
  return { latitude, longitude };
};

export const foresightMapFocusHref = (item) => {
  const gps = coordinatePairOf(item);
  const warningId = item?.warning_id
    || item?.id
    || item?.event_key
    || item?.external_id;
  if (!gps || !warningId) return '';
  const params = new URLSearchParams({
    focusWarning: String(warningId),
    lat: String(gps.latitude),
    lng: String(gps.longitude),
  });
  return `/map?${params.toString()}`;
};

const searchableText = (item) => {
  const sources = listOf(item?.sources).map((source) =>
    `${source?.name || ''} ${source?.display_name || ''} ${source?.origin_key || ''}`
  );
  const facts = listOf(item?.facts).map(textOf);
  const claims = listOf(item?.claims).map(textOf);
  const zones = listOf(
    item?.location?.affected_areas,
    item?.location?.affected_zones,
    item?.affected_areas,
    item?.affected_zones
  );
  return [
    displayTitleOf(item),
    item?.title,
    item?.description,
    hazardGateOf(item)?.reason,
    item?.source_name,
    detectingSourceLabel(detectingSourceOf(item)),
    isUnclassifiedPotentialHazard(item) ? 'unclassified potential hazard classification review' : '',
    item?.classification_review?.reason,
    ...itemHazardIds(item),
    ...sources,
    ...facts,
    ...claims,
    ...zones,
  ].join(' ').toLowerCase();
};

const detectionSeverityOf = (item) => String(
  hazardGateOf(item)?.severity ||
  item?.severity ||
  item?.classification?.severity ||
  item?.event_severity ||
  ''
).trim();

const detectionTone = (item) => {
  const severity = detectionSeverityOf(item).toLowerCase();
  if (severity === 'critical' || severity === 'high') return 'high';
  if (severity === 'medium') return 'medium';
  if (severity === 'low' || severity === 'informational') return 'low';
  return 'none';
};

const sourceSummary = (item) => {
  const sources = listOf(item?.sources);
  if (sources.length) {
    return sources
      .slice(0, 2)
      .map((source) => source?.display_name || source?.name || source?.origin_key)
      .filter(Boolean)
      .join(' + ');
  }
  return item?.connection?.name || item?.source_name || 'Connected external source';
};

const detectingSourceOf = (item) => {
  const explicit = item?.detecting_source;
  if (explicit && (typeof explicit === 'string' || typeof explicit === 'object')) return explicit;
  if (item?.connection) return item.connection;
  if (item?.source) return item.source;
  const sources = listOf(item?.sources);
  const marked = sources.find((source) =>
    source?.is_detector === true ||
    source?.is_detecting_source === true ||
    source?.is_primary_detection === true ||
    ['detector', 'detection', 'origin'].includes(String(source?.role || '').toLowerCase())
  );
  if (marked) return marked;
  if (sources.length) return sources[0];
  return item?.source_name || '';
};

const detectingSourceLabel = (source) => {
  if (typeof source === 'string') return source.trim();
  return String(
    source?.display_name ||
    source?.name ||
    source?.origin_name ||
    source?.origin_key ||
    source?.connection_id ||
    ''
  ).trim();
};

const detectingSourceKey = (source) => {
  if (typeof source === 'string') return source.trim().toLowerCase();
  const value =
    source?.connection_id ||
    source?.id ||
    source?.origin_key ||
    source?.display_name ||
    source?.name ||
    '';
  return String(value).trim().toLowerCase();
};

const itemDetectingSourceKey = (item) => detectingSourceKey(detectingSourceOf(item));

const sourceHealthValues = (payload) => {
  const health = payload?.source_health || {};
  const sources = listOf(payload?.sources);
  const healthy = summaryNumber(health, ['healthy', 'ok'], sources.filter((source) => source?.ok).length);
  const degraded = summaryNumber(
    health,
    ['degraded', 'unavailable', 'failed'],
    sources.filter((source) => source?.ok === false).length
  );
  const total = summaryNumber(health, ['total'], healthy + degraded);
  return { healthy, degraded, total };
};

const AreaSummary = ({ item, scope }) => {
  const location = item?.location || {};
  const rawZones = listOf(
    location.affected_areas,
    location.affected_zones,
    item?.affected_areas,
    item?.affected_zones
  );
  const nearest = location.nearest_zone || item?.nearest_zone;
  const labels = uniqueEventAreaLabels([rawZones, nearest], scope?.country);
  const distance = scope?.code === 'external'
    ? scope.distance
    : firstNumber(location.distance_km, item?.distance_km);
  const radius = firstNumber(
    location.coverage_radius_km,
    location.radius_km,
    item?.coverage_radius_km,
    item?.source_radius_km
  );
  const label = scope?.code === 'external' ? 'From Operations Area' : 'Operations Area';
  const primaryValue = distance !== null
    ? `${distance.toLocaleString()} km`
    : scope?.code === 'internal'
      ? 'Inside'
      : scope?.code === 'external'
        ? 'Outside · distance unresolved'
        : 'Distance unresolved';

  return (
    <div className="fdq-area" data-testid="fdq-affected-area">
      <small>{label}</small>
      <b>{primaryValue}</b>
      {labels.length > 0 && distance !== null && (
        <span>{labels.slice(0, 3).join(' · ')}</span>
      )}
      {radius !== null && <span>{radius.toLocaleString()} km mapped area</span>}
    </div>
  );
};

const ClassificationSummary = ({ item, scope }) => {
  const classification = primaryClassificationOf(item);
  const writtenLocation = writtenLocationOf(item);
  const mapFocusHref = foresightMapFocusHref(item);
  const locationLabel = writtenLocation || 'Location unresolved';
  return (
    <div className="fdq-classification" data-testid="fdq-classification">
      <div className="fdq-classification__path" aria-label="Incident Type, Event and Trigger">
        <span
          className={`fdq-classification__chip ${classification.incidentType ? '' : 'is-empty'}`}
          data-testid="fdq-classification-type"
        >
          <small>Incident Type</small>
          <b>{classification.incidentType || 'Not specified'}</b>
        </span>
        <span
          className={`fdq-classification__chip ${classification.event ? '' : 'is-empty'}`}
          data-testid="fdq-classification-event"
        >
          <small>Event</small>
          <b>{classification.event || 'Not specified'}</b>
        </span>
        <span
          className={`fdq-classification__chip ${classification.trigger ? '' : 'is-empty'}`}
          data-testid="fdq-classification-trigger"
        >
          <small>Trigger</small>
          <b>{classification.trigger || 'Not specified'}</b>
        </span>
      </div>
      <div className="fdq-classification__location" data-testid="fdq-written-location">
        <small>Location</small>
        {mapFocusHref ? (
          <a
            className="fdq-location-link"
            href={mapFocusHref}
            aria-label={`View ${locationLabel} on the main map`}
            title="Open this exact detected location on the main map"
            data-testid="fdq-location-map-link"
          >
            <b>{locationLabel}</b>
            <MapPin size={12} aria-hidden="true" />
          </a>
        ) : (
          <b>{locationLabel}</b>
        )}
      </div>
      <AreaSummary item={item} scope={scope} />
    </div>
  );
};

const EvidenceSummary = ({ item }) => {
  const claims = dedupeClaims(item?.claims);
  const activity = collectEvidenceActivity(item);
  const [activityExpanded, setActivityExpanded] = useState(false);
  const [claimsExpanded, setClaimsExpanded] = useState(false);
  const claimsAreUnresolved = claims.every((claim) =>
    ['Reported', 'Corroborated'].includes(claimStatusLabel(claim))
  );
  if (!activity.length && !claims.length) return null;

  return (
    <div className="fdq-evidence-grid">
      {activity.length > 0 && (
        <div className="fdq-evidence fdq-evidence--activity" data-testid="fdq-evidence-activity">
          <div className="fdq-evidence__title">
            <Layers3 size={14} aria-hidden="true" />
            <span>Evidence & activity</span>
            <small>{activity.length} linked {activity.length === 1 ? 'record' : 'records'}</small>
          </div>
          {(activityExpanded ? activity : activity.slice(0, 2)).map((entry, index) => (
            <div className="fdq-activity-row" key={`${entry.key}-${index}`}>
              <div className="fdq-activity-row__content">
                <div className="fdq-activity-row__meta">
                  <b>{entry.publisher || 'Source'}</b>
                  {entry.timestamp && (
                    <time dateTime={entry.timestamp}>{formatDateTime(entry.timestamp)}</time>
                  )}
                </div>
                {entry.url ? (
                  <a href={entry.url} target="_blank" rel="noreferrer" dir="auto">
                    {entry.title} <ExternalLink size={10} aria-hidden="true" />
                  </a>
                ) : (
                  <span dir="auto">{entry.title}</span>
                )}
              </div>
              <span className={`fdq-activity-status fdq-activity-status--${entry.status.toLowerCase().replaceAll(' ', '-')}`}>
                {entry.status}
              </span>
            </div>
          ))}
          {activity.length > 2 && (
            <button
              type="button"
              className="fdq-evidence__expand"
              onClick={() => setActivityExpanded((current) => !current)}
              aria-expanded={activityExpanded}
              data-testid="fdq-expand-activity"
            >
              {activityExpanded ? 'Show fewer' : `Show ${activity.length - 2} more`}
            </button>
          )}
        </div>
      )}

      {claims.length > 0 && (
        <div className="fdq-evidence fdq-evidence--claim" data-testid="fdq-unconfirmed-claims">
          <div className="fdq-evidence__title">
            <ShieldAlert size={14} aria-hidden="true" />
            {claimsAreUnresolved ? (
              <span>Not yet confirmed <small>Reported details</small></span>
            ) : (
              <span>Reported details <small>Source-attributed claim status</small></span>
            )}
          </div>
          {(claimsExpanded ? claims : claims.slice(0, 2)).map((claim, index) => {
            const source = claimSourceLabel(claim);
            const status = claimStatusLabel(claim);
            return (
              <div className="fdq-reported-detail" key={`${humanizeClaim(claim)}-${index}`}>
                <p>{humanizeClaim(claim)}</p>
                <div className="fdq-reported-detail__meta">
                  {source && <span>Source: {source}</span>}
                  <span className={`fdq-claim-status fdq-claim-status--${status.toLowerCase()}`}>{status}</span>
                </div>
              </div>
            );
          })}
          {claims.length > 2 && (
            <button
              type="button"
              className="fdq-evidence__expand"
              onClick={() => setClaimsExpanded((current) => !current)}
              aria-expanded={claimsExpanded}
              data-testid="fdq-expand-claims"
            >
              {claimsExpanded ? 'Show fewer' : `Show ${claims.length - 2} more`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const HazardCaseCard = ({
  item,
  index,
  operationsCountry,
  hazardCatalogById,
  onHazardSelect,
  onScenario,
  onReviewClassification,
}) => {
  const id = itemId(item, index);
  const classificationReviewRequired = isUnclassifiedPotentialHazard(item);
  const tone = detectionTone(item);
  const corroboration = firstNumber(
    item?.corroboration?.independent_count,
    item?.corroboration_count,
    item?.risk?.corroboration_count
  );
  const updates = firstNumber(item?.dedupe?.update_count, item?.update_count);
  const latency = formatLatency(
    item?.timing?.detection_latency_seconds ??
    item?.detection_latency_seconds ??
    item?.latency_seconds
  );
  const evidenceItems = collectEvidence(item);
  const officialSource = Boolean(
    item?.official_source ||
    [...listOf(item?.sources), ...evidenceItems]
      .some((source) => source?.official === true || source?.type === 'official' || source?.source_type === 'official')
  );
  const detector = detectingSourceOf(item);
  const detectorLabel = detectingSourceLabel(detector) || sourceSummary(item);
  const scope = operationalScopeOf(item, operationsCountry);
  const evidenceLabels = [...new Set([
    ...evidenceItems.map(evidencePublisher),
    ...listOf(item?.sources).map(detectingSourceLabel),
  ].filter((label) => label && label.toLocaleLowerCase() !== detectorLabel.toLocaleLowerCase()))];
  const shownEvidenceLabels = evidenceLabels.slice(0, 2);
  const hiddenEvidenceCount = Math.max(0, evidenceLabels.length - shownEvidenceLabels.length);

  return (
    <article
      className={[
        'fdq-card',
        `fdq-card--${tone}`,
        `fdq-card--scope-${scope.code}`,
        classificationReviewRequired ? 'fdq-card--classification-review' : '',
      ].filter(Boolean).join(' ')}
      data-testid={`fdq-card-${id}`}
      data-operational-scope={scope.code}
    >
      <div className="fdq-card__rail" aria-hidden="true" />
      <div className="fdq-card__body">
        <div className="fdq-card__top">
          <div className="fdq-card__heading">
            <div className="fdq-card__badges">
              <span
                className={`fdq-scope-badge fdq-scope-badge--${scope.code}`}
                data-testid={`fdq-scope-${id}`}
              >
                <b>{scope.label}</b>
                <small>{scope.detail}</small>
              </span>
              {officialSource && (
                <span className="fdq-official">Official evidence</span>
              )}
              {classificationReviewRequired && (
                <span
                  className="fdq-classification-review-badge"
                  data-testid={`fdq-unclassified-label-${id}`}
                >
                  <AlertTriangle size={12} aria-hidden="true" />
                  Unclassified Potential Hazard
                </span>
              )}
            </div>
            <h3>{displayTitleOf(item)}</h3>
            <p className="fdq-source-line">
              <span data-testid={`fdq-detecting-source-${id}`}>
                Detected by <b>{detectorLabel}</b>
                {shownEvidenceLabels.length > 0 && (
                  <> · Evidence: {shownEvidenceLabels.join(' · ')}{hiddenEvidenceCount > 0 && ` +${hiddenEvidenceCount}`}</>
                )}
              </span>
              <span aria-hidden="true">•</span>
              <time dateTime={eventTime(item) || undefined}>{formatDateTime(eventTime(item))}</time>
            </p>
          </div>

          <div className="fdq-card__metrics">
            {corroboration !== null && corroboration > 0 && (
              <span title="Independent evidence origins">
                <Layers3 size={13} aria-hidden="true" />
                {corroboration} independent {corroboration === 1 ? 'origin' : 'origins'}
              </span>
            )}
            {updates !== null && updates > 1 && (
              <span title="Updates merged into this event" data-testid="fdq-update-count">
                <RefreshCw size={13} aria-hidden="true" />
                {updates} updates merged
              </span>
            )}
            {latency && (
              <span title="Source publication to platform visibility" data-testid="fdq-detection-latency">
                <Clock3 size={13} aria-hidden="true" />
                Detected in {latency}
              </span>
            )}
          </div>
        </div>

        <ClassificationSummary item={item} scope={scope} />
        <EvidenceSummary item={item} />

        <div className="fdq-card__footer">
          <div className="fdq-card__context">
            {classificationReviewRequired ? (
              <span className="fdq-classification-review-note">
                No Hazard Library type has been assigned yet.
              </span>
            ) : (
              itemHazardIds(item).slice(0, 3).map((hazardId) => {
                const hazard = hazardCatalogById.get(hazardId);
                const label = hazard ? hazardLabelOf(hazard) : hazardId.replaceAll('_', ' ');
                return (
                  <button
                    key={hazardId}
                    type="button"
                    onClick={() => onHazardSelect(hazardId)}
                    aria-label={`Filter by ${label}`}
                    data-testid={`fdq-hazard-tag-${id}-${hazardId}`}
                  >
                    {label}
                  </button>
                );
              })
            )}
          </div>

          <div className="fdq-actions">
            {classificationReviewRequired ? (
              <button
                type="button"
                className="fdq-button fdq-button--classification-review"
                onClick={() => onReviewClassification(item)}
                aria-label={`Review classification for ${displayTitleOf(item)}`}
                data-testid={`fdq-review-classification-${id}`}
              >
                <AlertTriangle size={15} aria-hidden="true" />
                Review classification
              </button>
            ) : (
              <button
                type="button"
                className="fdq-button fdq-button--scenario"
                onClick={() => onScenario?.(item)}
                disabled={!onScenario}
                data-testid={`fdq-scenario-${id}`}
              >
                <Route size={15} aria-hidden="true" />
                Build Scenario
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

const ForesightDecisionQueue = ({
  data,
  error = '',
  loading = false,
  refreshing = false,
  onRefresh,
  onManage,
  onScenario,
  onClassificationReview,
  onAddConnection,
}) => {
  const payload = data || {};
  const unavailable = Boolean(error) && !data && !loading;
  const rawItems = useMemo(() => listOf(payload.items, payload.queue, payload.signals), [payload]);
  const items = useMemo(
    () => rawItems.filter(isActionableHazard),
    [rawItems]
  );
  const hazardCatalog = useMemo(() => listOf(payload.hazard_catalog), [payload]);
  const area = payload.operations_area || payload.area || {};
  const country = area.country_name || area.name ||
    (unavailable ? 'the active Operations Area' : 'your Operations Area');
  const [query, setQuery] = useState('');
  const [hazardFilter, setHazardFilter] = useState('all');
  const [detectorFilter, setDetectorFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [dictionaryOpen, setDictionaryOpen] = useState(false);
  const [dictionaryHazardId, setDictionaryHazardId] = useState('');
  const [visibleHazardIds, setVisibleHazardIds] = useState(loadVisibleHazardIds);

  const reviewClassification = (item) => {
    if (onClassificationReview) {
      onClassificationReview(item);
      return;
    }
    setDictionaryHazardId('');
    setDictionaryOpen(true);
  };

  const completeHazardCatalog = useMemo(() => {
    const liveById = new Map(
      hazardCatalog
        .map((hazard) => [hazardIdOf(hazard), hazard])
        .filter(([id]) => Boolean(id))
    );
    return HAZARD_FILTERS.map((definition) => ({
      ...definition,
      ...(liveById.get(definition.id) || {}),
      id: definition.id,
      label: liveById.get(definition.id)?.label || definition.label,
    }));
  }, [hazardCatalog]);

  const updateVisibleHazardIds = (ids) => {
    const validIds = new Set(HAZARD_FILTERS.map((hazard) => hazard.id));
    const next = [...new Set((ids || []).filter((id) => validIds.has(id)))];
    setVisibleHazardIds(next);
    try {
      window.localStorage.setItem(HAZARD_PREFS_KEY, JSON.stringify(next));
    } catch (error) {
      // Browser storage is a convenience; the queue remains usable without it.
    }
    if (hazardFilter !== 'all' && !next.includes(hazardFilter)) setHazardFilter('all');
  };

  const hazardCatalogById = useMemo(() => new Map(
    completeHazardCatalog
      .map((hazard) => [hazardIdOf(hazard), hazard])
      .filter(([id]) => Boolean(id))
  ), [completeHazardCatalog]);

  const visibleHazards = completeHazardCatalog.filter((hazard) =>
    visibleHazardIds.includes(hazardIdOf(hazard))
  );

  const selectedHazard = hazardFilter === 'all'
    ? null
    : hazardCatalogById.get(hazardFilter) || { id: hazardFilter, label: hazardFilter.replaceAll('_', ' ') };

  const detectorOptions = useMemo(() => {
    const byKey = new Map();
    items.forEach((item) => {
      const source = detectingSourceOf(item);
      const key = detectingSourceKey(source);
      const label = detectingSourceLabel(source);
      if (!key || !label) return;
      const current = byKey.get(key);
      byKey.set(key, { key, label, count: (current?.count || 0) + 1 });
    });
    return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);

  const selectedDetector = detectorOptions.find((source) => source.key === detectorFilter);
  const scopeCounts = useMemo(() => ({
    all: items.length,
    internal: items.filter((item) => operationalScopeOf(item, country).code === 'internal').length,
    external: items.filter((item) => operationalScopeOf(item, country).code === 'external').length,
  }), [country, items]);
  const scopeFilteredItems = scopeFilter === 'all'
    ? items
    : items.filter((item) => operationalScopeOf(item, country).code === scopeFilter);
  const detectorScopedItems = detectorFilter === 'all'
    ? scopeFilteredItems
    : scopeFilteredItems.filter((item) => itemDetectingSourceKey(item) === detectorFilter);

  const visibleItems = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter((item) =>
      (scopeFilter === 'all' || operationalScopeOf(item, country).code === scopeFilter) &&
      (detectorFilter === 'all' || itemDetectingSourceKey(item) === detectorFilter) &&
      (hazardFilter === 'all' || itemHazardIds(item).includes(hazardFilter)) &&
      (!needle || searchableText(item).includes(needle))
    );
  }, [country, detectorFilter, hazardFilter, items, query, scopeFilter]);

  const selectedHazardTotal = selectedHazard
    ? detectorScopedItems.filter((item) => itemHazardIds(item).includes(hazardFilter)).length
    : null;
  const countForHazard = (hazard) => {
    const id = hazardIdOf(hazard);
    return detectorFilter === 'all'
      ? scopeFilteredItems.filter((item) => itemHazardIds(item).includes(id)).length
      : detectorScopedItems.filter((item) => itemHazardIds(item).includes(id)).length;
  };
  const activeVisibleHazards = visibleHazards.filter((hazard) =>
    countForHazard(hazard) > 0 || hazardIdOf(hazard) === hazardFilter
  );
  const inactiveVisibleHazards = visibleHazards.filter((hazard) =>
    countForHazard(hazard) === 0 && hazardIdOf(hazard) !== hazardFilter
  );

  const health = sourceHealthValues(payload);
  const radius = firstNumber(area.radius_km, payload.aoi_radius_km);
  const updatedAt = payload.generated_at || payload.updated_at;

  if (unavailable) {
    return (
      <section className="fdq" data-testid="foresight-decision-queue">
        <header className="fdq-header">
          <div className="fdq-header__mark" aria-hidden="true">
            <Radio size={23} />
          </div>
          <div className="fdq-header__copy">
            <h2>ForeSight Detection Feed</h2>
            <p>Live hazard detection for <b>{country}</b> is temporarily unavailable.</p>
          </div>
          <div className="fdq-header__status">
            <div className="fdq-health fdq-health--unavailable" data-testid="fdq-source-unavailable">
              <AlertTriangle size={14} aria-hidden="true" />
              <b>Live data unavailable</b>
            </div>
          </div>
          <div className="fdq-header__controls">
            <div className="fdq-header__actions">
              {onManage && (
                <button type="button" onClick={onManage} className="fdq-icon-button">
                  <Settings2 size={17} aria-hidden="true" />
                  Manage Connections
                </button>
              )}
              <button
                type="button"
                onClick={onRefresh}
                disabled={!onRefresh || refreshing}
                className="fdq-icon-button fdq-icon-button--refresh"
              >
                <RefreshCw size={17} className={refreshing ? 'fdq-spin' : ''} aria-hidden="true" />
                Retry
              </button>
            </div>
          </div>
        </header>
        <div className="fdq-service-error" role="alert" data-testid="fdq-service-error">
          <AlertTriangle size={20} aria-hidden="true" />
          <div>
            <b>ForeSight could not load live detection data</b>
            <span>{error}</span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="fdq" data-testid="foresight-decision-queue">
      <header className="fdq-header">
        <div className="fdq-header__mark" aria-hidden="true">
          <Radio size={23} />
        </div>
        <div className="fdq-header__copy">
          <h2>ForeSight Detection Feed</h2>
          <p>
            Validated detections, mapped locations and linked evidence · <b>{country}</b>
            {radius !== null && <> · {radius.toLocaleString()} km watch area</>}
          </p>
        </div>
        <div className="fdq-header__status">
          <span className="fdq-header__status-label">Sources</span>
          <div className="fdq-health" data-testid="fdq-source-health">
            <span className="fdq-health__total">
              <b>{health.total}</b>
              <small>connected</small>
            </span>
            <span className="fdq-health__breakdown">
              <span className="fdq-health__healthy"><i aria-hidden="true" /> {health.healthy} healthy</span>
              <span className="fdq-health__degraded"><i aria-hidden="true" /> {health.degraded} degraded</span>
            </span>
          </div>
        </div>
        <div className="fdq-header__controls">
          <div className="fdq-header__actions">
            {onManage && (
              <button type="button" onClick={onManage} className="fdq-icon-button">
                <Settings2 size={17} aria-hidden="true" />
                Manage Connections
              </button>
            )}
            <button
              type="button"
              onClick={onRefresh}
              disabled={!onRefresh || refreshing}
              className="fdq-icon-button fdq-icon-button--refresh"
              data-testid="fdq-refresh"
            >
              <RefreshCw size={17} className={refreshing ? 'fdq-spin' : ''} aria-hidden="true" />
              Refresh
            </button>
          </div>
          {updatedAt && <time dateTime={updatedAt}>Updated {formatDateTime(updatedAt)}</time>}
        </div>
      </header>

      {error && (
        <div
          className="fdq-service-error fdq-service-error--stale"
          role="alert"
          data-testid="fdq-service-error"
        >
          <AlertTriangle size={19} aria-hidden="true" />
          <div>
            <b>Live refresh failed</b>
            <span>{error} · Showing the last successfully loaded result.</span>
          </div>
          {onRefresh && (
            <button type="button" onClick={onRefresh} disabled={refreshing}>
              <RefreshCw size={15} className={refreshing ? 'fdq-spin' : ''} aria-hidden="true" />
              Retry
            </button>
          )}
        </div>
      )}

      <div className="fdq-control-panel">
        <div className="fdq-toolbar fdq-toolbar--simple">
          <div
            className="fdq-scope-filter"
            data-testid="fdq-scope-filter"
            role="group"
            aria-label="Filter detections by operational scope"
          >
            {[
              { code: 'all', label: 'All' },
              { code: 'internal', label: 'Internal' },
              { code: 'external', label: 'External' },
            ].map((option) => (
              <button
                key={option.code}
                type="button"
                className={`fdq-scope-filter__button fdq-scope-filter__button--${option.code}`}
                onClick={() => setScopeFilter(option.code)}
                aria-pressed={scopeFilter === option.code}
                data-testid={`fdq-scope-filter-${option.code}`}
              >
                {option.label} <span>{scopeCounts[option.code]}</span>
              </button>
            ))}
          </div>

          <div className="fdq-toolbar__tools">
            <label className="fdq-search">
              <Search size={16} aria-hidden="true" />
              <span className="fdq-sr-only">Search detected hazard cases</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search cases, locations or evidence"
                data-testid="fdq-search"
              />
            </label>

            <label className="fdq-detector-filter">
              <span>Detected by</span>
              <select
                value={detectorFilter}
                onChange={(event) => setDetectorFilter(event.target.value)}
                data-testid="fdq-detector-filter"
                aria-label="Filter cases by the source that first detected them"
              >
                <option value="all">All sources ({items.length})</option>
                {detectorOptions.map((source) => (
                  <option key={source.key} value={source.key}>
                    {source.label} ({source.count})
                  </option>
                ))}
              </select>
            </label>

            <button
              type="button"
              className="fdq-dictionary-button"
              onClick={() => {
                setDictionaryHazardId('');
                setDictionaryOpen(true);
              }}
              data-testid="fdq-hazard-dictionary"
            >
              <BookOpen size={14} aria-hidden="true" />
              Hazard Dictionary
              <span>{visibleHazardIds.length}/{completeHazardCatalog.length}</span>
            </button>
          </div>
        </div>

        <div className="fdq-filter-row">
          {completeHazardCatalog.length > 0 && (
            <>
              <span className="fdq-filter-row__label">Hazard</span>
              <div
                className="fdq-hazard-chips"
                data-testid="fdq-hazard-filter"
                role="group"
                aria-label="Filter detected cases by hazard"
              >
                <button
                  type="button"
                  onClick={() => setHazardFilter('all')}
                  data-testid="fdq-hazard-filter-all"
                  aria-pressed={hazardFilter === 'all'}
                  className="fdq-hazard-chip fdq-hazard-chip--all"
                >
                  All hazards <span>{detectorScopedItems.length}</span>
                </button>
                {activeVisibleHazards.map((hazard) => {
                  const id = hazardIdOf(hazard);
                  const count = countForHazard(hazard);
                  const active = hazardFilter === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setHazardFilter(active ? 'all' : id)}
                      data-testid={`fdq-hazard-filter-${id}`}
                      aria-pressed={active}
                      title={`${count} ${count === 1 ? 'case' : 'cases'} · ${hazardCoverageText(hazard)}`}
                      className="fdq-hazard-chip"
                      style={{ '--fdq-hazard-color': HAZARD_COLORS.get(id) || '#64748b' }}
                    >
                      <i aria-hidden="true" /> {hazardLabelOf(hazard)} <span>{count}</span>
                    </button>
                  );
                })}
                {inactiveVisibleHazards.length > 0 && (
                  <details className="fdq-inactive-hazards">
                    <summary>
                      {inactiveVisibleHazards.length} hazard {inactiveVisibleHazards.length === 1 ? 'type' : 'types'} with no activity
                    </summary>
                    <div className="fdq-inactive-hazards__menu">
                      {inactiveVisibleHazards.map((hazard) => {
                        const id = hazardIdOf(hazard);
                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => setHazardFilter(id)}
                            data-testid={`fdq-hazard-filter-${id}`}
                            aria-pressed="false"
                            title={`0 cases · ${hazardCoverageText(hazard)}`}
                            className="fdq-hazard-chip is-empty"
                            style={{ '--fdq-hazard-color': HAZARD_COLORS.get(id) || '#64748b' }}
                          >
                            <i aria-hidden="true" /> {hazardLabelOf(hazard)} <span>0</span>
                          </button>
                        );
                      })}
                    </div>
                  </details>
                )}
              </div>
              <div className="fdq-detection-count" data-testid="fdq-detection-count">
                <Radio size={15} aria-hidden="true" />
                <span><b>{visibleItems.length}</b> detections shown</span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="fdq-list" aria-live="polite">
        {loading && items.length === 0 ? (
          <div className="fdq-loading" data-testid="fdq-loading">
            <RefreshCw size={22} className="fdq-spin" aria-hidden="true" />
            <b>Validating detected hazards…</b>
            <span>Checking titles, classifications, locations and evidence.</span>
          </div>
        ) : selectedHazard && selectedHazardTotal === 0 ? (
          <div className="fdq-hazard-empty" data-testid="fdq-hazard-empty">
            <div className="fdq-hazard-empty__icon" aria-hidden="true">
              <ShieldAlert size={21} />
            </div>
            <div>
              <b>No current {hazardLabelOf(selectedHazard)} cases</b>
              <span>
                {selectedDetector
                  ? `${selectedDetector.label} has not detected a current case for this hazard. Other sources may still appear as evidence inside matching cases.`
                  : selectedHazard?.coverage?.connected
                  ? 'Connected sources are currently quiet for this hazard.'
                  : 'No active source coverage is currently reporting this hazard.'}
              </span>
            </div>
            {!selectedDetector && (
              <button
                type="button"
                onClick={() => {
                  setDictionaryHazardId(hazardIdOf(selectedHazard));
                  setDictionaryOpen(true);
                }}
                data-testid="fdq-manage-hazard"
              >
                <Settings2 size={15} aria-hidden="true" />
                Manage sources
              </button>
            )}
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="fdq-empty" data-testid="fdq-empty">
            <CheckCircle2 size={25} aria-hidden="true" />
            <b>No hazard cases match this view</b>
            <span>Adjust the scope, detecting source, hazard or search filter.</span>
          </div>
        ) : (
          visibleItems.map((item, index) => (
            <HazardCaseCard
              key={itemId(item, index)}
              item={item}
              index={index}
              hazardCatalogById={hazardCatalogById}
              onHazardSelect={setHazardFilter}
              onScenario={onScenario}
              onReviewClassification={reviewClassification}
              operationsCountry={country}
            />
          ))
        )}
      </div>

      <footer className="fdq-footer">
        <span><MapPin size={13} aria-hidden="true" /> Locations come from source coordinates or validated place resolution; unresolved detections stay unmapped.</span>
      </footer>

      {dictionaryOpen && (
        <HazardDictionaryManager
          hazardCatalog={completeHazardCatalog}
          items={detectorScopedItems}
          preferItemCounts={detectorFilter !== 'all'}
          initialHazardId={dictionaryHazardId}
          visibleHazardIds={visibleHazardIds}
          onVisibleHazardIdsChange={updateVisibleHazardIds}
          onFilter={(hazardId) => {
            setHazardFilter(hazardId || 'all');
          }}
          onClose={() => {
            setDictionaryOpen(false);
            setDictionaryHazardId('');
          }}
          onRefresh={onRefresh}
          onAddConnection={onAddConnection}
        />
      )}
    </section>
  );
};

export default ForesightDecisionQueue;
